import { FormattedMessage, defineMessage, defineMessages } from 'react-intl';
import styled from 'styled-components';

import { Sport } from '@sorare/core/src/__generated__/globalTypes';
import { BodyM, BodyS } from '@sorare/core/src/atoms/typography';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import {
  laptopAndAbove,
  tabletAndAbove,
} from '@sorare/core/src/style/mediaQuery';

import { StarBallExponent } from 'components/forYou/RecommendationTitle/StarBallExponent';

import cards from './assets/cards.jpg';
import cash from './assets/cash.jpg';
import irl_baseball from './assets/irl_baseball.jpg';
import irl_football from './assets/irl_football.jpg';
import irl_nba from './assets/irl_nba.jpg';
import jersey from './assets/jersey.jpg';
import merch from './assets/merch.jpg';
import tickets from './assets/tickets.jpg';

const Wrapper = styled.div`
  width: 100%;
  max-width: 320px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: repeat(4, minmax(25%, 1fr));
  grid-template-rows: repeat(2, 1fr);
  grid-column-gap: 2px;
  grid-row-gap: 2px;
  @media ${tabletAndAbove} {
    max-width: 440px;
  }
`;

const Block = styled.div<{ background?: string }>`
  background-size: cover;
  background-position: center;
  background-image: url(${({ background }) => background});
  padding: var(--unit);
  display: flex;
  justify-content: center;
  text-align: center;
`;
const SmallBlock = styled(Block)<{ background?: string }>`
  aspect-ratio: 78 / 113;
`;
const Irl = styled(Block)`
  grid-area: 1 / 1 / 3 / 3;
  border-top-left-radius: var(--unit);
  border-bottom-left-radius: var(--unit);
`;

const Cash = styled(SmallBlock)`
  grid-area: 1 / 3 / 2 / 4;
`;

const Tickets = styled(SmallBlock)`
  grid-area: 1 / 4 / 2 / 5;
  border-top-right-radius: var(--unit);
`;
const Merch = styled(SmallBlock)`
  grid-area: 2 / 3 / 3 / 4;
`;
const Card = styled(SmallBlock)`
  grid-area: 2 / 4 / 3 / 5;
`;

export const NoWrap = styled.span`
  white-space: nowrap;
`;

const defaultTitle = defineMessage({
  defaultMessage: 'Real Life <nowrap>Experiences{starBall}</nowrap>',
  id: 'components.forYou.FirstTime.Rewards.irlExperiences',
});

const titles = defineMessages({
  [Sport.FOOTBALL]: defineMessage({
    defaultMessage: 'Meet <nowrap>Zidane{starBall}</nowrap>',
    id: 'components.forYou.FirstTime.Rewards.meetZidane',
  }),
  [Sport.NBA]: defaultTitle,
  [Sport.BASEBALL]: defaultTitle,
});

const BlockTitle = styled(BodyS)`
  @media ${tabletAndAbove} {
    font: var(--t-label-s);
  }
  @media ${laptopAndAbove} {
    font: var(--t-body-m);
  }
  color: var(--c-neutral-1000);
`;

const BlockTitleL = styled(BodyM)`
  text-align: left;
  align-self: flex-start;
  @media ${tabletAndAbove} {
    font: var(--t-label-s);
  }
  @media ${laptopAndAbove} {
    font: var(--t-body-l);
  }
`;

export const Rewards = () => {
  const { sport = Sport.FOOTBALL } = useSportContext();
  const irl = {
    [Sport.FOOTBALL]: irl_football,
    [Sport.NBA]: irl_nba,
    [Sport.BASEBALL]: irl_baseball,
  }[sport];

  const merchBg = {
    [Sport.FOOTBALL]: jersey,
    [Sport.NBA]: merch,
    [Sport.BASEBALL]: merch,
  }[sport];

  return (
    <Wrapper>
      <Irl background={irl}>
        <BlockTitleL>
          <FormattedMessage
            {...titles[sport]}
            values={{
              nowrap: chunks => <NoWrap>{chunks}</NoWrap>,
              starBall: <StarBallExponent size={12} />,
            }}
          />
        </BlockTitleL>
      </Irl>
      <Cash background={cash}>
        <BlockTitle>
          <FormattedMessage
            defaultMessage="Cash"
            id="components.forYou.FirstTime.Rewards.cash"
          />
        </BlockTitle>
      </Cash>
      <Tickets background={tickets}>
        <BlockTitle>
          <FormattedMessage
            defaultMessage="Tickets"
            id="components.forYou.FirstTime.Rewards.tickets"
          />
        </BlockTitle>
      </Tickets>
      <Merch background={merchBg}>
        <BlockTitle>
          <FormattedMessage
            defaultMessage="Jerseys"
            id="components.forYou.FirstTime.Rewards.jerseys"
          />
        </BlockTitle>
      </Merch>
      <Card background={cards}>
        <BlockTitle>
          <FormattedMessage
            defaultMessage="Cards"
            id="components.forYou.FirstTime.Rewards.cards"
          />
        </BlockTitle>
      </Card>
    </Wrapper>
  );
};
